import type {
  CycleMedianStatusDto,
  TargetPriceSourceDto,
  TargetPriceSummaryRowDto,
} from '@/api/campaign-analysis-client';
import { csvEscapeCell } from '@/lib/param-format';
import { seniorityLabel } from '@/lib/rank-labels';

// =============================================================================
// target-price-summary-export.ts — Writer PUR du tableau de fin de campagne « Prix cible et médianes par cycle »
// (lot L9, DBU v2.7 §2.6 « Fichiers d'export (CSV) », EF-BEN-12, ST-BENCH-07).
// =============================================================================
// Sérialise les lignes `TargetPriceSummaryRowDto` (déjà en mémoire, lues via useTargetPriceSummary) en CSV
// (RFC 4180 + anti-injection-formule OBS-1 via csvEscapeCell). Grain COUPLE (profil × niveau), distinct du
// grain trinôme du ranking : ce tableau est exporté en FICHIER CSV DÉDIÉ depuis sa propre card.
//
// ⚠️ LOT U1 (2026-09-17, ADR-FUSION-ANALYSE-RANG-01, décision 12) : le classeur XLSX de la page ne porte plus
// que DEUX feuilles (une par case cochée : « Distribution anonymisée », « Ranking nominatif »). La feuille
// « Prix cible et médianes » n'y est donc PLUS ajoutée, et le writer de feuille (appendTargetPriceSummarySheet)
// est retiré faute de consommateur. Le tableau reste exporté en CSV dédié, journalisé.
//
// JOURNAL : chaque export reste journalisé par l'écran via POST /admin/positioning/:id/export/record
// (DE.BenchmarkExported, descripteur non nominatif) avec `selectedColumns` = clés de
// TARGET_PRICE_SUMMARY_EXPORT_COLUMNS et `rowCount` = nombre de couples.
//
// Règles d'affichage : valeur absente ⇒ CELLULE VIDE (jamais 0) ; C2 d'une campagne mono-cycle ⇒ « Sans objet »
// (médiane ET effectif), distinct d'un C2 sans réponse (médiane vide, effectif 0).

/** Clé de colonne stable doublée de son libellé FR d'en-tête. */
export interface TargetPriceSummaryExportColumn {
  readonly key: string;
  readonly label: string;
}

/** Colonnes exportées, ORDRE FIGÉ (ST-BENCH-07 + traçabilité de la pose). */
export const TARGET_PRICE_SUMMARY_EXPORT_COLUMNS: readonly TargetPriceSummaryExportColumn[] = [
  { key: 'family', label: 'Famille' },
  { key: 'profileCode', label: 'Profil' },
  { key: 'seniorityLevel', label: 'Niveau' },
  { key: 'targetPriceEur', label: 'Prix cible' },
  { key: 'priceSource', label: 'Source' },
  { key: 'defaultEurProposed', label: 'Valeur proposée' },
  { key: 'validatedAt', label: 'Date de pose' },
  { key: 'medianC1Eur', label: 'Médiane C1' },
  { key: 'countC1', label: 'n C1' },
  { key: 'medianC2Eur', label: 'Médiane C2' },
  { key: 'countC2', label: 'n C2' },
  { key: 'gapMedianC2VsTargetEur', label: 'Écart médiane C2 / prix cible (€)' },
  { key: 'gapMedianC2VsTargetPct', label: 'Écart médiane C2 / prix cible (%)' },
];

/** Libellé d'un cycle sans objet (C2 d'une campagne mono-cycle). */
export const CYCLE_NOT_APPLICABLE_LABEL = 'Sans objet';

const SOURCE_LABELS: Readonly<Record<TargetPriceSourceDto, string>> = {
  PulseDefault: 'Valeur Pulse',
  AdminEntry: 'Saisie admin',
};

/** Médiane d'un cycle : « Sans objet » si le cycle n'existe pas, vide si aucune réponse. */
function medianCell(status: CycleMedianStatusDto, value: number | null): string | number {
  if (status === 'NotApplicable') return CYCLE_NOT_APPLICABLE_LABEL;
  return value === null ? '' : value;
}

/** Effectif d'un cycle : « Sans objet » si le cycle n'existe pas, sinon l'effectif (0 si aucune réponse). */
function countCell(status: CycleMedianStatusDto, value: number | null): string | number {
  if (status === 'NotApplicable') return CYCLE_NOT_APPLICABLE_LABEL;
  return value ?? 0;
}

/** Valeur BRUTE d'une cellule (string ou number ; absent ⇒ ''). */
function cellValue(key: string, row: TargetPriceSummaryRowDto): string | number {
  switch (key) {
    case 'family':
      return row.family ?? '';
    case 'profileCode':
      return row.profileCode;
    case 'seniorityLevel':
      return seniorityLabel(row.seniorityLevel);
    case 'targetPriceEur':
      return row.targetPriceEur ?? '';
    case 'priceSource':
      return row.priceSource === null ? '' : SOURCE_LABELS[row.priceSource];
    case 'defaultEurProposed':
      return row.defaultEurProposed ?? '';
    case 'validatedAt':
      return row.validatedAt === null ? '' : row.validatedAt.slice(0, 10);
    case 'medianC1Eur':
      return medianCell(row.c1Status, row.medianC1Eur);
    case 'countC1':
      return countCell(row.c1Status, row.countC1);
    case 'medianC2Eur':
      return medianCell(row.c2Status, row.medianC2Eur);
    case 'countC2':
      return countCell(row.c2Status, row.countC2);
    case 'gapMedianC2VsTargetEur':
      return row.gapMedianC2VsTargetEur ?? '';
    case 'gapMedianC2VsTargetPct':
      return row.gapMedianC2VsTargetPct ?? '';
    default:
      return '';
  }
}

/** Tableau de tableaux (en-tête + lignes) : source unique du CSV. */
export function buildTargetPriceSummaryAoa(
  rows: readonly TargetPriceSummaryRowDto[],
): (string | number)[][] {
  return [
    TARGET_PRICE_SUMMARY_EXPORT_COLUMNS.map((col) => col.label),
    ...rows.map((row) => TARGET_PRICE_SUMMARY_EXPORT_COLUMNS.map((col) => cellValue(col.key, row))),
  ];
}

/** CSV RFC 4180 (lignes CRLF), chaque cellule passée par csvEscapeCell (OBS-1). */
export function buildTargetPriceSummaryCsv(rows: readonly TargetPriceSummaryRowDto[]): string {
  return buildTargetPriceSummaryAoa(rows)
    .map((line) => line.map((cell) => csvEscapeCell(String(cell))).join(','))
    .join('\r\n');
}

/** Nom de fichier horodaté du CSV dédié. */
export function targetPriceSummaryExportFileName(now: Date = new Date()): string {
  return `prix-cible-medianes-${now.toISOString().slice(0, 10)}.csv`;
}
